import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { FaPaperclip, FaSearch } from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { TicketContext } from '../../../app/providers/TicketContext';
import { fetchWrapper } from '../../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';

const RightPanelSendBox = () => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [originalTime, setOriginalTime] = useState(''); // DATE input için saat bilgisini sakla
  const { SendEvent, selectedTicketId, currentInputField, isSendingEvent, pendingCheckboxSelection, setPendingCheckboxSelection } = useContext(TicketContext);
  const inputRef = useRef(null); // Input referansı için
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchTimeoutRef = useRef(null);
  const [lastSearchRequest, setLastSearchRequest] = useState(null);
  const [lastSearchResponse, setLastSearchResponse] = useState(null);
  const [searchCount, setSearchCount] = useState(null);
  const autoFetchKeyRef = useRef('');
  
  // Pagination modal state'leri
  const [showPaginationModal, setShowPaginationModal] = useState(false);
  const [paginationData, setPaginationData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isLoadingPagination, setIsLoadingPagination] = useState(false);
  const [paginationSearchQuery, setPaginationSearchQuery] = useState('');
  const [paginationTotalCount, setPaginationTotalCount] = useState(0);
  const [paginationSortCol, setPaginationSortCol] = useState(null);
  const [paginationSortDir, setPaginationSortDir] = useState('asc');
  
  // Geçici test: her durumda enabled
  const isDisabled = false;

  const searchTable = useMemo(() => {
    const table = currentInputField?.SEARCHTABLE;
    return table ? String(table).trim() : '';
  }, [currentInputField]);

  const searchFields = useMemo(() => {
    const raw = currentInputField?.DFIELD;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(String).map((v) => v.trim()).filter(Boolean);
      }
      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).map(String).map((v) => v.trim()).filter(Boolean);
      }
    } catch {
      // fallback to comma separated
    }
    return String(raw).split(',').map((v) => v.trim()).filter(Boolean);
  }, [currentInputField]);

  const searchFilters = useMemo(() => {
    const raw = currentInputField?.FILTER;
    if (!raw) return null;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
        return parsed;
      }
    } catch {
      // invalid JSON
    }
    return null;
  }, [currentInputField]);

  const isSearchEnabled = Boolean(searchTable && searchFields.length > 0);
  const maxAll = useMemo(() => {
    const raw = Number(process.env.REACT_APP_ES_MAX_ALL || 500);
    if (Number.isNaN(raw) || raw <= 0) return 500;
    return Math.min(raw, 2000);
  }, []);
  const lowCountThreshold = useMemo(() => {
    const raw = Number(process.env.REACT_APP_ES_LOW_COUNT_THRESHOLD || maxAll);
    if (Number.isNaN(raw) || raw <= 0) return maxAll;
    return Math.min(raw, 2000);
  }, [maxAll]);
  const autoFetchThreshold = 30;
  const searchValueField = useMemo(() => {
    return searchFields[0] || '';
  }, [searchFields]);
  const searchLabelField = useMemo(() => {
    return searchFields[1] || searchValueField;
  }, [searchFields, searchValueField]);
  const searchHintLabel = useMemo(() => {
    if (!currentInputField) return 'Bu alan';
    return currentInputField.ELMDC || currentInputField.FNAME || 'Bu alan';
  }, [currentInputField]);

  const filterChips = useMemo(() => {
    if (!searchFilters || typeof searchFilters !== 'object') return [];
    return Object.entries(searchFilters).map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }));
  }, [searchFilters]);
  
  // currentInputField.FVALUE varsa input value'suna set et
  useEffect(() => {
    if (currentInputField && currentInputField.FVALUE !== undefined && currentInputField.FVALUE !== null && currentInputField.FVALUE !== '') {
      let fvalue = String(currentInputField.FVALUE);
      
      // Eğer DATE tipindeyse ve datetime formatında geliyorsa (2025-11-05 12:43:08)
      // sadece tarih kısmını al (2025-11-05) ve saat bilgisini sakla
      if (currentInputField.DTYPE === 'DATE' && fvalue.includes(' ')) {
        const parts = fvalue.split(' ');
        setOriginalTime(parts[1] || '00:00:00'); // Saat bilgisini sakla
        fvalue = parts[0]; // Sadece tarih kısmını al
      } else if (currentInputField.DTYPE === 'DATETIME' && fvalue.includes(' ')) {
        // DATETIME tipinde datetime formatını datetime-local formatına çevir
        // 2025-11-05 12:43:08 -> 2025-11-05T12:43
        const parts = fvalue.split(' ');
        const timePart = parts[1] ? parts[1].substring(0, 5) : '00:00'; // Saniyeyi çıkar
        fvalue = `${parts[0]}T${timePart}`;
        setOriginalTime(''); // DATETIME'da originalTime kullanılmaz
      } else {
        setOriginalTime('00:00:00'); // Saat bilgisi yoksa varsayılan saat
      }
      
      setMessage(fvalue);
    }
  }, [currentInputField]);

  useEffect(() => {
    setSearchResults([]);
    setSearchError('');
    setShowSearchPanel(false);
    setHighlightedIndex(-1);
    setSearchCount(null);
    autoFetchKeyRef.current = '';
  }, [searchTable, currentInputField?.DFIELD]);

  const loadSearchCount = useCallback(async () => {
    if (!isSearchEnabled) return;
    try {
      const processId = process.env.REACT_APP_ES_SEARCH_PROCESS || '100023346';
      const requestPayload = {
        token: localStorage.getItem('token'),
        table: searchTable,
        fields: searchFields.join(','),
        count_only: true,
      };
      if (searchFilters) {
        requestPayload.filter = searchFilters;
      }
      const response = await fetchWrapper(
        processId,
        requestPayload,
        navigate,
      );
      const count = response?.CSResponse?.count;
      if (typeof count === 'number') {
        setSearchCount(count);
      }
    } catch {
      // ignore count failures
    }
  }, [isSearchEnabled, searchFields, searchTable, searchFilters, navigate]);

  useEffect(() => {
    loadSearchCount();
  }, [loadSearchCount]);

  // Message temizlendikten sonra input'a focus yap
  useEffect(() => {
    if (message === '' && inputRef.current && !isDisabled) {
      // Kısa bir gecikme ile focus yap ki DOM güncellemesi tamamlansın
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [message, isDisabled]);

  const runSearch = useCallback(async (query) => {
    if (!isSearchEnabled) return;
    const minLen = searchCount !== null && searchCount <= lowCountThreshold ? 1 : 2;
    if (!query || query.trim().length < minLen) {
      // Eğer otomatik fetch yapıldıysa sonuçları koruyalım
      if (autoFetchKeyRef.current) {
        return;
      }
      setSearchResults([]);
      setShowSearchPanel(false);
      setSearchError('');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    try {
      const processId = process.env.REACT_APP_ES_SEARCH_PROCESS || '100023346';
      const requestPayload = {
        token: localStorage.getItem('token'),
        table: searchTable,
        fields: searchFields.join(','),
        q: query.trim(),
        limit: 20,
      };
      if (searchFilters) {
        requestPayload.filter = searchFilters;
      }
      setLastSearchRequest(requestPayload);
      const response = await fetchWrapper(
        processId,
        requestPayload,
        navigate,
      );
      setLastSearchResponse(response);
      if (response?.responsecode && response.responsecode !== 0) {
        setSearchError(response?.responsetext || 'Arama hatası');
        setSearchResults([]);
        setShowSearchPanel(true);
        return;
      }
      const results = response?.CSResponse || response?.results || [];
      setSearchResults(Array.isArray(results) ? results : []);
      setShowSearchPanel(true);
    } catch (error) {
      setSearchError(error?.message || 'Arama hatası');
      setLastSearchResponse({ error: error?.message || 'Arama hatası' });
      setSearchResults([]);
      setShowSearchPanel(true);
    } finally {
      setIsSearching(false);
    }
  }, [isSearchEnabled, searchFields, searchTable, searchFilters, navigate, searchCount, lowCountThreshold]);

  // Otomatik fetch - sadece küçük dropdown için
  const runFetchAll = useCallback(async () => {
    if (!isSearchEnabled) return;
    setIsSearching(true);
    setSearchError('');
    try {
      const processId = process.env.REACT_APP_ES_SEARCH_PROCESS || '100023346';
      const requestPayload = {
        token: localStorage.getItem('token'),
        table: searchTable,
        fields: searchFields.join(','),
        q: '',
        limit: maxAll,
        fetch_all: true,
        max_all: maxAll,
      };
      if (searchFilters) {
        requestPayload.filter = searchFilters;
      }
      setLastSearchRequest(requestPayload);
      const response = await fetchWrapper(
        processId,
        requestPayload,
        navigate,
      );
      setLastSearchResponse(response);
      if (response?.responsecode && response.responsecode !== 0) {
        setSearchError(response?.responsetext || 'Arama hatası');
        setSearchResults([]);
        setShowSearchPanel(true);
        return;
      }
      const results = response?.CSResponse || response?.results || [];
      setSearchResults(Array.isArray(results) ? results : []);
      setShowSearchPanel(true);
    } catch (error) {
      setSearchError(error?.message || 'Arama hatası');
      setLastSearchResponse({ error: error?.message || 'Arama hatası' });
      setSearchResults([]);
      setShowSearchPanel(true);
    } finally {
      setIsSearching(false);
    }
  }, [isSearchEnabled, searchFields, searchTable, searchFilters, navigate, maxAll]);

  // "Tümünü gör" butonu için - pagination modal aç
  const openPaginationModal = useCallback(async () => {
    if (!isSearchEnabled) return;
    // Modal'ı aç ve ilk sayfayı yükle
    setShowPaginationModal(true);
    setCurrentPage(1);
    setPaginationSearchQuery('');
    setPaginationTotalCount(searchCount || 0);
    loadPaginatedData(1, pageSize, '');
  }, [isSearchEnabled, pageSize, searchCount]);

  const loadPaginatedData = useCallback(async (page, size, searchQuery = '') => {
    if (!isSearchEnabled) return;
    setIsLoadingPagination(true);
    setSearchError('');
    try {
      const processId = process.env.REACT_APP_ES_SEARCH_PROCESS || '100023346';
      const requestPayload = {
        token: localStorage.getItem('token'),
        table: searchTable,
        fields: searchFields.join(','),
        q: searchQuery,
        limit: size,
        fetch_all: searchQuery === '',
        max_all: size,
        offset: (page - 1) * size,
      };
      if (searchFilters) {
        requestPayload.filter = searchFilters;
      }
      
      if (searchQuery !== '') {
        const countPayload = {
          token: localStorage.getItem('token'),
          table: searchTable,
          fields: searchFields.join(','),
          count_only: true,
        };
        if (searchFilters) {
          countPayload.filter = searchFilters;
        }
        const countResponse = await fetchWrapper(
          processId,
          countPayload,
          navigate,
        );
        const count = countResponse?.CSResponse?.count || 0;
        setPaginationTotalCount(count);
      } else {
        setPaginationTotalCount(searchCount || 0);
      }
      
      setLastSearchRequest(requestPayload);
      const response = await fetchWrapper(
        processId,
        requestPayload,
        navigate,
      );
      setLastSearchResponse(response);
      if (response?.responsecode && response.responsecode !== 0) {
        setSearchError(response?.responsetext || 'Arama hatası');
        setPaginationData([]);
        return;
      }
      const results = response?.CSResponse || response?.results || [];
      setPaginationData(Array.isArray(results) ? results : []);
    } catch (error) {
      setSearchError(error?.message || 'Arama hatası');
      setLastSearchResponse({ error: error?.message || 'Arama hatası' });
      setPaginationData([]);
    } finally {
      setIsLoadingPagination(false);
    }
  }, [isSearchEnabled, searchFields, searchTable, searchFilters, navigate, searchCount]);

  useEffect(() => {
    if (!isSearchEnabled) return;
    if (typeof searchCount !== 'number') return;
    if (searchCount > autoFetchThreshold) return;
    if (!searchTable || searchFields.length === 0) return;
    const key = `${searchTable}::${searchFields.join(',')}`;
    if (autoFetchKeyRef.current === key) return;
    autoFetchKeyRef.current = key;
    runFetchAll();
  }, [isSearchEnabled, searchCount, searchTable, searchFields, runFetchAll, autoFetchThreshold]);

  useEffect(() => {
    if (!isSearchEnabled || isDisabled) return;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      runSearch(message);
    }, 250);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [message, runSearch, isSearchEnabled, isDisabled]);
  
  // Date input için özel onChange handler
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    setMessage(selectedDate);
    // originalTime zaten saklanmış durumda, submit ederken birleştirilecek
  };
  


  // Input field'ın nasıl render edileceğini belirle
  const renderInputField = () => {
    if (!currentInputField) {
      return (
        <input
          ref={inputRef}
          type="text"
          placeholder="Mesaj yazın..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          className="w-full text-slate-700 bg-transparent focus:outline-none text-[11.5px] placeholder:text-slate-400 placeholder:font-medium disabled:cursor-not-allowed disabled:opacity-50"
        />
      );
    }

    const { ELMDC, DTYPE, DFLEN, REQUIRED, ELMNT } = currentInputField;
    


    // Input tipini belirle
    let inputType = 'text';
    // Placeholder'ı daha belirgin yap - kullanıcı ne gireceğini anlasın
    let placeholder = ELMDC || (DTYPE ? `${DTYPE} girin...` : 'Mesaj yazın...');
    let step = undefined;
    let min = undefined;
    
    switch (DTYPE) {
      case 'DATE':
        inputType = 'date';
        break;
      case 'DATETIME':
        inputType = 'datetime-local';
        break;
      case 'INT':
        inputType = 'number';
        step = '1';
        min = '0';
        break;
      case 'DECIMAL':
      case 'FLOAT':
        inputType = 'number';
        step = '0.01';
        min = '0';
        break;
      case 'FILE':
        inputType = 'file';
        break;
      case 'TEXT':
      default:
        inputType = 'text';
        break;
    }

    // FILE tipi için özel file input render et
    if (DTYPE === 'FILE') {
      return (
        <input
          type="file"
          multiple
          placeholder={placeholder}
          onChange={handleFileChange}
          disabled={isDisabled}
          className="w-full text-slate-700 bg-transparent focus:outline-none text-[11.5px] disabled:cursor-not-allowed disabled:opacity-50"
          required={REQUIRED === 'REQUIRED'}
        />
      );
    }

    // Diğer input tipleri için normal input render et
    return (
      <input
        ref={inputRef}
        type={inputType}
        placeholder={placeholder}
        value={message}
        onChange={DTYPE === 'DATE' ? handleDateChange : (e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        className="w-full text-slate-700 bg-transparent focus:outline-none text-[11.5px] placeholder:text-slate-400 placeholder:font-medium disabled:cursor-not-allowed disabled:opacity-50"
        required={REQUIRED === 'REQUIRED'}
        maxLength={DFLEN || 512}
        step={step}
        min={min}
      />
    );
  };

  // Dosya seçimi işlemi
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = (valueToSend, extraData) => {
    if (isDisabled) return;

    // Checkbox seçimi varsa onu FVALUE olarak gönder, normal input'u yoksay
    if (pendingCheckboxSelection && pendingCheckboxSelection.values.length > 0) {
      const fvalue = pendingCheckboxSelection.values.join(',');
      const messageData = {
        inputName: pendingCheckboxSelection.FNAME,
        message: fvalue,
        data: JSON.stringify(pendingCheckboxSelection.rawRows),
      };
      SendEvent(messageData, [], 'RightPanelSendBox-CheckboxSelection');
      setPendingCheckboxSelection(null);
      setMessage('');
      setFiles([]);
      return;
    }
    
    // Normal gönderim
    let finalMessage = (typeof valueToSend === 'string') ? valueToSend : message;
    
    if (currentInputField && currentInputField.DTYPE === 'DATE' && originalTime && finalMessage) {
      finalMessage = `${finalMessage} ${originalTime}`;
    }
    
    if (currentInputField && currentInputField.DTYPE === 'DATETIME' && finalMessage && finalMessage.includes('T')) {
      const datetimeParts = finalMessage.split('T');
      finalMessage = `${datetimeParts[0]} ${datetimeParts[1]}:00`;
    }

    // ES seçimlerinde tüm satır extraData olarak gelir → backend'e `data`
    // alanında JSON olarak iletilir. FNAME/FVALUE'a dokunulmaz.
    const dataPayload = (extraData !== undefined && extraData !== null)
      ? (typeof extraData === 'string' ? extraData : JSON.stringify(extraData))
      : undefined;

    if (!currentInputField) {
      const messageData = {
        inputName: 'message',
        message: finalMessage,
        ...(dataPayload !== undefined ? { data: dataPayload } : {}),
      };
      SendEvent(messageData, files, 'RightPanelSendBox');
    } else {
      const messageData = {
        inputName: currentInputField.ELMNT || currentInputField.FNAME,
        message: finalMessage,
        ...(dataPayload !== undefined ? { data: dataPayload } : {}),
      };
      SendEvent(messageData, files, 'RightPanelSendBox');
    }

    setMessage('');
    setFiles([]);
    
    // Input'a focus yap ki kullanıcı tekrar yazmaya devam edebilsin
    // requestAnimationFrame kullanarak DOM güncellemesinden sonra focus yapıyoruz
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (inputRef.current && !isDisabled) {
          inputRef.current.focus();
        }
      });
    });
  };

  // Enter tuşuna basıldığında mesaj gönderme işlemi
  const handleKeyPress = (e) => {
    if (isDisabled) return; // CTABL doluysa veya SendEvent çalışıyorsa işlem yapma
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation(); // Event propagation'ı durdur
      handleSubmit();
    }
  };

  // Popup kaldırıldığı için ArrowUp/Down + Enter ile satır seçme akışı yok artık.
  // (Eski popup'a özel klavye navigation handler'ı kasıtlı olarak no-op yapıldı.)
  const handleKeyDown = () => {};

  // Sayfa değişimi
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadPaginatedData(newPage, pageSize, paginationSearchQuery);
  };

  // Sayfa boyutu değişimi
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadPaginatedData(1, newSize, paginationSearchQuery);
  };

  // Pagination içinde arama
  const handlePaginationSearch = (query) => {
    setPaginationSearchQuery(query);
    setCurrentPage(1);
    loadPaginatedData(1, pageSize, query);
  };

  // Toplam sayfa sayısı
  const totalPages = paginationTotalCount ? Math.ceil(paginationTotalCount / pageSize) : 0;

  const sortedPaginationData = useMemo(() => {
    if (!paginationSortCol || !paginationData.length) return paginationData;
    return [...paginationData].sort((a, b) => {
      const av = a?.[paginationSortCol] ?? '';
      const bv = b?.[paginationSortCol] ?? '';
      const na = Number(av), nb = Number(bv);
      if (!isNaN(na) && !isNaN(nb) && av !== '' && bv !== '') return paginationSortDir === 'asc' ? na - nb : nb - na;
      return paginationSortDir === 'asc'
        ? String(av).localeCompare(String(bv), 'tr')
        : String(bv).localeCompare(String(av), 'tr');
    });
  }, [paginationData, paginationSortCol, paginationSortDir]);

  const handlePaginationSort = useCallback((col) => {
    if (paginationSortCol === col) {
      setPaginationSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setPaginationSortCol(col);
      setPaginationSortDir('asc');
    }
  }, [paginationSortCol]);

  const exportPaginationXLSX = useCallback(() => {
    if (!sortedPaginationData.length) return;
    const headers = ['#', ...searchFields];
    const rows = sortedPaginationData.map((r, i) =>
      [((currentPage - 1) * pageSize) + i + 1, ...searchFields.map((f) => r?.[f] != null ? String(r[f]) : '')]
    );
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h, i) => ({
      wch: Math.min(Math.max(String(h).length, ...rows.map((r) => String(r[i] || '').length)) + 2, 50),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `es_export_${searchTable || 'data'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [sortedPaginationData, searchFields, searchTable, currentPage, pageSize]);

  const renderFileInput = () => {
    
    // Eğer current input field FILE tipindeyse ataç ikonunu gizle
    if (currentInputField && currentInputField.DTYPE === 'FILE') {
      return null;
    }
    
    return (
      <div className="flex-shrink-0 ml-2">
        <input
          type="file"
          multiple
          id="file-upload"
          onChange={handleFileChange}
          disabled={isDisabled}
          className="hidden"
        />
        <label
          htmlFor="file-upload"
          className={`flex items-center justify-center w-8 h-8 transition-colors ${
            isDisabled 
              ? 'cursor-not-allowed text-slate-300' 
              : 'cursor-pointer text-slate-500 hover:text-slate-700'
          }`}
        >
          <FaPaperclip className="text-base" />
        </label>
      </div>
    );
  };

  return (
    <div className={`relative w-full border-t border-slate-200/70 bg-white/90 ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Popup (autocomplete dropdown) kaldırıldı — sonuçlar artık yalnızca pagination modal'dan görüntülenir.
          Sonuç varsa input'un solundaki search ikonu yanıp söner. */}
      {isSearchEnabled && typeof searchCount === 'number' && searchCount > autoFetchThreshold && (
        <div className="pointer-events-none absolute left-3 right-3 top-0 z-10 -translate-y-full sm:left-4 sm:right-4">
          <div className="pointer-events-auto relative inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600 shadow-md">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-50 animate-ping"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
            </span>
            <span className="font-semibold text-slate-700">{searchHintLabel}</span>
            <span className="text-slate-500">için {searchCount.toLocaleString('tr-TR')} kayıt var.</span>
            <button
              type="button"
              onClick={openPaginationModal}
              disabled={isSearching}
              className="ml-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              title="Tümünü getir"
            >
              Tümünü gör
            </button>
            <button
              type="button"
              onClick={() => setSearchCount(null)}
              className="ml-auto text-slate-300 hover:text-slate-500 transition-colors p-0.5 flex-shrink-0"
              title="Kapat"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            {(searchFields.length > 0 || filterChips.length > 0) && (
              <div className="flex w-full flex-col gap-1 pt-1 border-t border-slate-100 mt-0.5">
                <div className="text-[9px] text-slate-400 leading-snug">
                  <span className="font-medium">{searchFields.join(', ')}</span> alan{searchFields.length > 1 ? 'larında' : 'ında'} arama yapılır
                  {filterChips.length > 0 && (
                    <span> ve <span className="font-medium">{filterChips.map(c => `${c.key}: ${c.value}`).join(', ')}</span> filtre{filterChips.length > 1 ? 'leri' : 'si'} uygulanır</span>
                  )}
                  .
                </div>
                <div className="text-[8.5px] text-slate-300 italic leading-snug">
                  Search by <span className="font-medium not-italic">{searchFields.join(', ')}</span>
                  {filterChips.length > 0 && (
                    <span> filtered by <span className="font-medium not-italic">{filterChips.map(c => `${c.key}=${c.value}`).join(', ')}</span></span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {searchFields.map((field) => (
                    <span key={`sf-${field}`} className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-600 border border-blue-100">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="flex-shrink-0"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>
                      {field}
                    </span>
                  ))}
                  {filterChips.map((chip) => (
                    <span key={`fc-${chip.key}`} className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700 border border-amber-100">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="flex-shrink-0"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>
                      {chip.key}={chip.value}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <span className="pointer-events-none absolute -bottom-1.5 left-4 h-3 w-3 rotate-45 border-b border-r border-slate-200 bg-white" />
          </div>
        </div>
      )}
      {/* Checkbox Seçim Bar — tablo checkbox'larından seçilen satırlar */}
      {pendingCheckboxSelection && pendingCheckboxSelection.values.length > 0 && (
        <div className="mx-3 sm:mx-4 mb-1 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-[11px]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-semibold text-blue-700">
              {pendingCheckboxSelection.values.length} satır seçildi
              {pendingCheckboxSelection.FNAME && (
                <span className="ml-1.5 font-normal text-blue-500">({pendingCheckboxSelection.FNAME})</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setPendingCheckboxSelection(null)}
              className="text-blue-400 hover:text-blue-600 transition-colors"
              title="Seçimi temizle"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
            {pendingCheckboxSelection.values.map((val, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-full bg-blue-100 border border-blue-200 px-2 py-0.5 text-[10px] font-medium text-blue-700"
              >
                {val}
              </span>
            ))}
          </div>
          <p className="mt-1.5 text-[9.5px] text-blue-400">
            Gönder butonuna basarak seçilen değerleri ilet.
          </p>
        </div>
      )}

      {/* Input Alanı - WhatsApp tarzı */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2">  
        <div className={`flex items-center flex-1 bg-slate-100 rounded-full px-3 py-1.5 min-h-[38px] border border-slate-200/70 ${isDisabled ? 'cursor-not-allowed' : ''}`}>
          {isSearchEnabled && (() => {
            const hasResults = !isSearching && !searchError && searchResults.length > 0;
            const resultCount = searchResults.length;
            return (
              <button
                type="button"
                onClick={openPaginationModal}
                className={`relative mr-2 flex h-6 w-6 items-center justify-center rounded-full shadow-sm transition-all cursor-pointer ${
                  hasResults
                    ? 'bg-blue-500 text-white animate-pulse hover:bg-blue-600'
                    : 'bg-white text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                }`}
                title={
                  isSearching
                    ? 'Aranıyor…'
                    : hasResults
                    ? `${resultCount} sonuç bulundu — listeyi aç`
                    : 'Tümünü gör'
                }
              >
                <FaSearch className="text-[11px]" />
                {hasResults && (
                  <>
                    {/* Yanıp sönen halka */}
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-blue-400 opacity-60 animate-ping" />
                    {/* Sonuç sayısı rozeti */}
                    <span className="pointer-events-none absolute -top-1.5 -right-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm ring-1 ring-white">
                      {resultCount > 99 ? '99+' : resultCount}
                    </span>
                  </>
                )}
              </button>
            );
          })()}
          {renderInputField()}
          {renderFileInput()}
        </div>
        <button
          onClick={() => handleSubmit()}
          disabled={isDisabled}
          className={`flex-shrink-0 w-9 h-9 rounded-full text-white flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md ${
            isDisabled 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          <MdSend className="text-base" />
        </button>
      </div>

      {/* Dosya Listesi */}
      {files.length > 0 && (
        <div className="px-3 sm:px-4 pb-2 flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <div
              key={index}
              className="bg-slate-100 px-2.5 py-1 text-[11px] rounded-full text-slate-600 border border-slate-200"
            >
              <span className="truncate max-w-[110px]">{file.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Modal */}
      {showPaginationModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ width: '90vw', maxWidth: '1152px', height: '85vh' }}>
            {/* Modal Header - Kesin sabit yükseklik */}
            <div className="border-b border-slate-200 px-6 py-4" style={{ height: '140px' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    {searchHintLabel} - Tüm Kayıtlar
                  </h2>
                  <p className="text-sm text-slate-500">
                    {paginationSearchQuery ? `${paginationTotalCount} kayıt bulundu` : `Toplam ${searchCount} kayıt`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportPaginationXLSX}
                    disabled={!sortedPaginationData.length}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#217346] text-[#217346] bg-white rounded-lg text-xs font-semibold hover:bg-[#217346] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Excel olarak dışa aktar"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    XLSX
                  </button>
                  <button
                    onClick={() => setShowPaginationModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-1"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={paginationSearchQuery}
                  onChange={(e) => handlePaginationSearch(e.target.value)}
                  placeholder="Tabloda ara..."
                  className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                {paginationSearchQuery && (
                  <button
                    onClick={() => handlePaginationSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xl"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Modal Body - Kesin calculated height */}
            <div className="overflow-hidden relative" style={{ height: 'calc(85vh - 140px - 100px)' }}>
              {/* Table Container - Kesin sabit boyut, her durumda aynı */}
              <div className="w-full h-full overflow-auto p-6">
                {/* Tüm durumlar için wrapper - Tam genişlik ve yükseklik */}
                <div className="w-full h-full">
                  {isLoadingPagination ? (
                    <div className="flex flex-col items-center justify-center w-full h-full min-h-[400px]">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                      <div className="text-slate-500 text-sm">Yükleniyor...</div>
                    </div>
                  ) : searchError ? (
                    <div className="flex items-center justify-center w-full h-full min-h-[400px]">
                      <div className="text-red-500 text-sm">{searchError}</div>
                    </div>
                  ) : paginationData.length === 0 ? (
                    <div className="flex items-center justify-center w-full h-full min-h-[400px]">
                      <div className="text-slate-400 text-sm">Kayıt bulunamadı</div>
                    </div>
                  ) : (
                    <div className="w-full">
                      <table className="w-full border-collapse text-sm" style={{ fontFamily: "Calibri, Arial, sans-serif" }}>
                        <thead className="bg-[#e7e6e6] sticky top-0 z-10">
                          <tr>
                            <th className="border border-[#c0c0c0] px-2 py-2 text-center font-semibold text-[#333] whitespace-nowrap w-12 text-xs">
                              #
                            </th>
                            {searchFields.map((field, idx) => {
                              const isActive = paginationSortCol === field;
                              return (
                                <th
                                  key={`header-${field}-${idx}`}
                                  onClick={() => handlePaginationSort(field)}
                                  className="border border-[#c0c0c0] px-4 py-2 text-left font-semibold text-[#333] whitespace-nowrap cursor-pointer select-none hover:bg-[#d4d4d4] transition-colors"
                                >
                                  <span className="inline-flex items-center gap-1.5">
                                    {field}
                                    {isActive && (
                                      <span className="text-[#217346] text-[10px] font-bold">{paginationSortDir === 'asc' ? '▲' : '▼'}</span>
                                    )}
                                  </span>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPaginationData.map((item, rowIndex) => (
                            <tr
                              key={`row-${rowIndex}`}
                              onClick={() => {
                                const value = item?.[searchValueField] ?? '';
                                const valueStr = value ? String(value) : '';
                                setMessage(valueStr);
                                setShowPaginationModal(false);
                                // Seçilen satırın TÜM alanları `data` olarak gönderilir.
                                handleSubmit(valueStr, item);
                              }}
                              className="cursor-pointer transition-colors duration-100"
                              style={{ background: rowIndex % 2 === 0 ? '#fff' : '#fafafa' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#e8f4f8'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = rowIndex % 2 === 0 ? '#fff' : '#fafafa'; }}
                            >
                              <td className="border border-[#e0e0e0] px-2 py-1.5 text-center text-xs text-[#888] font-semibold bg-[#e7e6e6]">
                                {((currentPage - 1) * pageSize) + rowIndex + 1}
                              </td>
                              {searchFields.map((field, cellIndex) => (
                                <td
                                  key={`cell-${rowIndex}-${cellIndex}`}
                                  className="border border-[#e0e0e0] px-4 py-1.5 text-[#333]"
                                >
                                  {item?.[field] !== undefined && item?.[field] !== null
                                    ? String(item?.[field])
                                    : '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer - Pagination Controls - Kesin sabit yükseklik */}
            <div className="border-t border-slate-200 px-6 py-4 bg-slate-50" style={{ height: '100px' }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                {/* Sayfa Boyutu Seçimi */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">Sayfa başına:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-3 py-2 border-2 border-slate-400 bg-white text-slate-800 font-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  >
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* Pagination */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border-2 border-slate-700 text-slate-800 font-semibold rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800 hover:text-white transition-colors duration-200"
                  >
                    İlk
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border-2 border-slate-700 text-slate-800 font-semibold rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800 hover:text-white transition-colors duration-200"
                  >
                    Önceki
                  </button>
                  
                  <span className="px-4 py-2 text-sm font-bold text-slate-900 bg-slate-200 rounded-lg">
                    Sayfa {currentPage} / {totalPages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border-2 border-slate-700 text-slate-800 font-semibold rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800 hover:text-white transition-colors duration-200"
                  >
                    Sonraki
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border-2 border-slate-700 text-slate-800 font-semibold rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800 hover:text-white transition-colors duration-200"
                  >
                    Son
                  </button>
                </div>

                {/* Bilgi */}
                <div className="text-sm font-medium text-slate-800">
                  {paginationTotalCount > 0 
                    ? `${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, paginationTotalCount)} arası gösteriliyor (Toplam: ${paginationTotalCount})`
                    : 'Kayıt bulunamadı'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default RightPanelSendBox;
